import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import type { FileWithPath } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import { useFormikContext } from 'formik'
import { TransitionGroup } from 'react-transition-group'
import type { TransitionProps } from '@mui/material/transitions'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Fade from '@mui/material/Fade'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { keyframes } from '@mui/material/styles'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import useTranslate from '@/hooks/use-translate'
import DialogUploadValues from '@/types/dialogUploadValues'
import { MAX_DOSSIER_NAME_LENGTH } from '@/constants/NameLengths'
import { filterFileTypes } from '@/utils'
import { NfFileRow } from '../primitives/NfFileRow'
import { NfSectionHeading } from '../primitives/NfSectionHeading'
import { getNf, nf } from '../nfTokens'

/** À partir de ce nombre de fichiers, la zone de dépôt se replie en barre pour laisser la place à la liste. */
export const FILES_COMPACT_AT = 3
/** Décalage entre deux lignes d'un même dépôt, plafonné pour qu'un gros lot n'attende pas. */
const STAGGER_MS = 35
const STAGGER_MAX_STEPS = 8

const rowEnter = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(.985); }
  to { opacity: 1; transform: none; }
`

export interface FilesFieldProps {
  useZip?: boolean
}

type FileEntry = { id: number; delay: number }

/**
 * Clé stable par fichier : une clé tirée de l'index ferait rejouer l'entrée des
 * lignes suivantes à chaque retrait.
 */
const useFileEntries = (files: File[]) => {
  const entriesRef = useRef(new WeakMap<File, FileEntry>())
  const nextIdRef = useRef(0)
  let newInBatch = 0
  return files.map((file) => {
    let entry = entriesRef.current.get(file)
    if (!entry) {
      entry = {
        id: nextIdRef.current++,
        delay: Math.min(newInBatch++, STAGGER_MAX_STEPS) * STAGGER_MS,
      }
      entriesRef.current.set(file, entry)
    }
    return entry
  })
}

type FileListItemProps = Partial<TransitionProps> & {
  delay: number
  reducedMotion: boolean
  children: ReactNode
}

/** Ligne de la liste : se déplie à l'entrée, se replie à la sortie ; un simple fondu si le mouvement est réduit. */
const FileListItem: FC<FileListItemProps> = ({ delay, reducedMotion, children, ...transition }) => {
  if (reducedMotion) {
    return (
      <Fade {...transition} timeout={120}>
        <Box sx={{ pb: 0.75 }}>{children}</Box>
      </Fade>
    )
  }
  return (
    <Collapse {...transition} timeout={{ enter: 260, exit: 200 }} style={{ transitionDelay: transition.in ? `${delay}ms` : '0ms' }}>
      <Box
        sx={{
          pb: 0.75,
          animation: `${rowEnter} 360ms cubic-bezier(.34, 1.36, .64, 1) ${delay}ms backwards`,
        }}
      >
        {children}
      </Box>
    </Collapse>
  )
}

/**
 * Étape « Fichiers » du wizard : zone de dépôt toujours visible, avec en
 * dessous la liste des fichiers ajoutés dès qu'il y en a.
 */
export const FilesField: FC<FilesFieldProps> = ({
  useZip,
}) => {
  const { t: tCommon } = useTranslate('common')
  const { t: tUpload } = useTranslate('upload')
  const formik = useFormikContext<DialogUploadValues>()
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const files = useMemo<FileWithPath[]>(
    () => formik.values.files as FileWithPath[],
    [formik.values.files]
  )
  const entries = useFileEntries(files)

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: filterFileTypes({
      tCommon,
      callback: (filteredAcceptedFiles: File[]) => {
        formik.setFieldValue('files', files.concat(filteredAcceptedFiles))
      },
      useZip,
    }),
  })

  const removeFile = useCallback(
    (index: number) => {
      if (files.length > index) {
        const newFiles = [...files]
        newFiles.splice(index, 1)
        formik.setFieldValue('files', newFiles)
      }
    },
    [files]
  )

  useEffect(() => {
    if (files.length > 0 && !formik.values.recordName) {
      let base = files[0].name.split('.').slice(0, -1).join('.')
      if (base.length > MAX_DOSSIER_NAME_LENGTH) base = base.slice(0, MAX_DOSSIER_NAME_LENGTH)
      formik.setFieldValue('recordName', base, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  const hasFiles = files.length > 0
  const compact = files.length >= FILES_COMPACT_AT

  return (
    <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <NfSectionHeading>{tUpload('wizardStepFilesLabel')}</NfSectionHeading>
      <Stack spacing={0.75} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box
        {...getRootProps()}
        data-testid="dropzone"
        data-compact={compact || undefined}
        sx={(theme) => {
          const t = getNf(theme)
          return {
            display: 'flex',
            flexDirection: compact ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: compact ? 'flex-start' : 'center',
            gap: compact ? 1.25 : 0.75,
            flex: '0 0 auto',
            minHeight: hasFiles ? undefined : 140,
            p: compact ? '10px 14px' : '34px 20px',
            cursor: 'pointer',
            textAlign: compact ? 'left' : 'center',
            border: `1px dashed ${isDragActive ? t.green : t.dash}`,
            bgcolor: isDragActive ? t.greenTint : t.surfaceMuted,
            borderRadius: '12px',
            outline: 'none',
            transition: 'background-color .14s, border-color .14s, padding .22s ease',
            '&:focus-visible': { boxShadow: `0 0 0 3px ${t.greenTint2}` },
            '&:hover .nf-drop-icon': { transform: 'translateY(-2px)' },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'background-color .14s, border-color .14s',
              '&:hover .nf-drop-icon': { transform: 'none' },
            },
          }
        }}
      >
        <input
          {...getInputProps()}
          data-testid="dropzone-input"
          accept=".pdf, .jpeg, .jpg, .png, .xls, .xlsx, .ods"
        />
        <Box
          className="nf-drop-icon"
          sx={(theme) => {
            const t = getNf(theme)
            return {
              width: compact ? 28 : 34,
              height: compact ? 28 : 34,
              borderRadius: `${nf.radiusCard}px`,
              bgcolor: t.surface,
              border: `1px solid ${isDragActive ? t.green : t.line}`,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              color: 'primary.main',
              transform: isDragActive ? 'translateY(-4px)' : 'none',
              boxShadow: isDragActive ? `0 6px 14px -6px ${t.green}` : 'none',
              transition: 'transform .28s cubic-bezier(.34, 1.56, .64, 1), box-shadow .2s, border-color .14s',
              '@media (prefers-reduced-motion: reduce)': {
                transform: 'none',
                transition: 'border-color .14s',
              },
            }
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: compact ? 15 : 17 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={(theme) => ({ fontSize: 12, fontWeight: 600, color: getNf(theme).ink })}>
            {isDragActive
              ? tUpload('wizardDropTitleActive')
              : compact
                ? tUpload('wizardAddFiles')
                : tUpload('wizardDropTitle')}
          </Typography>
          <Typography
            sx={(theme) => ({
              fontSize: 10.5,
              color: getNf(theme).muted,
              mt: compact ? '2px' : 0.75,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: compact ? 'nowrap' : undefined,
            })}
          >
            {tUpload('wizardDropSubtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Toujours montée : le dernier fichier retiré se replie comme les autres. */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          <TransitionGroup component={null}>
            {files.map((file, index) => (
              <FileListItem key={entries[index].id} delay={entries[index].delay} reducedMotion={reducedMotion}>
                <Box data-testid={`file-row-${index}`}>
                  <NfFileRow file={file} onRemove={() => removeFile(index)} />
                </Box>
              </FileListItem>
            ))}
          </TransitionGroup>
      </Box>
      </Stack>
    </Stack>
  )
}

export default FilesField
